<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-error">
        <i class="far fa-list"></i> System Error Log
      </Tab>
      <Tab tabid="tab-warning">
        <i class="far fa-list"></i> System Warning Log
      </Tab>
      <Tab tabid="tab-info-api">
        <i class="far fa-database"></i> API
      </Tab>
      <Tab tabid="tab-info-reporting-api">
        <i class="far fa-database"></i> Reporting API
      </Tab>
      <Tab tabid="tab-info-ui">
        <i class="far fa-database"></i> UI
      </Tab>
      <Tab tabid="tab-sockets">
        <i class="far fa-router"></i> Sockets
      </Tab>
      <Tab tabid="tab-jobqueue">
        <i class="far fa-clipboard-list"></i> Job Queue
      </Tab>
      <Tab tabid="tab-automation">
        <i class="far fa-rocket"></i> Automations
      </Tab>
      <Tab tabid="tab-cache">
        <i class="far fa-database"></i> Cache
      </Tab>
      <Tab tabid="tab-tools">
        <i class="far fa-tools"></i> Tools
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-error">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu class="float-right">
              <DropdownMenuItem @click="reloadSystemLogs" icon="fa-sync">Refresh</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>System Error Logs</ContentTitle>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-12">
            <StaticDataTable class="static-table-list" smallRows>
              <template v-slot:header>
                <tr>
                  <th>Type</th>
                  <th>Group</th>
                  <th>Time</th>
                  <th>Description</th>
                  <th>Details</th>
                  <th>Action</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="log in systemErrorLogs" :key="log.id" :class="{'system-log-highlight': isSystemLogToday(log)}">
                  <td><LabelBadge :labelClass="systemLogLabelClassForType(log.type)">{{log.type}}</LabelBadge></td>
                  <td>{{log.logGroup}}</td>
                  <td>{{log.createdAt | formatDateCustom('YYYY/MM/DD - h:mma')}}</td>
                  <td>{{log.description}}</td>
                  <td>{{truncateLogDetails(log.details)}}</td>
                  <td><FormButton @click="displaySystemLog($event, log)" class="btn btn-default btn-xxs">Show</FormButton></td>
                </tr>
              </template>
            </StaticDataTable>
          </div>
        </div>
        <Popover ref="logSystemPopover" sizeClass="popover-xlarge" placement="left" :showHeader="true" :headerTitle="selectedLog.description" :scrollable="true">
          <div class="p-4 well debug-output">
            {{selectedLog.details}}
          </div>
        </Popover>
      </TabPage>

      <TabPage tabid="tab-warning">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu class="float-right">
              <DropdownMenuItem @click="reloadSystemLogs" icon="fa-sync">Refresh</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>System Warning Logs</ContentTitle>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-12">
            <StaticDataTable class="static-table-list" smallRows>
              <template v-slot:header>
                <tr>
                  <th>Type</th>
                  <th>Group</th>
                  <th>Time</th>
                  <th>Description</th>
                  <th>Details</th>
                  <th>Action</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="log in systemWarningLogs" :key="log.id">
                  <td><LabelBadge :labelClass="systemLogLabelClassForType(log.type)">{{log.type}}</LabelBadge></td>
                  <td>{{log.logGroup}}</td>
                  <td>{{log.createdAt | formatDateCustom('YYYY/MM/DD - h:mma')}}</td>
                  <td>{{log.description}}</td>
                  <td>{{truncateLogDetails(log.details)}}</td>
                  <td><FormButton @click="displayWarningLog($event, log)" class="btn btn-default btn-xxs">Show</FormButton></td>
                </tr>
              </template>
            </StaticDataTable>

            <Popover ref="logWarningPopover" sizeClass="popover-xlarge" placement="left" :showHeader="true" :headerTitle="selectedLog.description" :scrollable="true">
              <div class="p-4 well debug-output">
                {{selectedLog.details}}
              </div>
            </Popover>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-info-api">
        <div v-for="(info, index) of systemInfoList" :key="index" class="row feed-element">
          <div class="font-weight-bold col-3 col-form-label">{{info.name}}</div>
          <div class="col-9 well debug-output">{{info.value}}</div>
        </div>
      </TabPage>

      <TabPage tabid="tab-info-reporting-api">
        <div v-for="(info, index) of systemReportingInfoList" :key="index" class="row feed-element">
          <div class="font-weight-bold col-3 col-form-label">{{info.name}}</div>
          <div class="col-9 well debug-output">{{info.value}}</div>
        </div>
      </TabPage>

      <TabPage tabid="tab-info-ui">
        <div class="row feed-element">
          <div class="font-weight-bold col-3 col-form-label">Frontend</div>
          <div class="col-9 well debug-output">{{frontendDetails}}</div>
        </div>
      </TabPage>
      
      <TabPage tabid="tab-sockets">
        <ContentTitle>Socket List</ContentTitle>

        <CollapsibleTable>
          <template v-slot:header>
            <tr>
              <th>Socket</th>
              <th width="50%">Stats</th>
            </tr>
          </template>
          <template v-slot:body>
            <template v-for="(conn, index) in socketConnList">
              <CollapsibleRow :key="index">
                <td class="font-weight-bold" @click="conn.expanded = !conn.expanded">
                  <CollapsibleRowArrow :expanded="conn.expanded" />
                  {{conn.id}}
                </td>
                <td @click="conn.expanded = !conn.expanded">
                  Rooms: {{conn.rooms.length}}
                </td>
              </CollapsibleRow>
              <tr v-for="(room, roomindex) in conn.rooms" :key="`${conn.id}-${room}`" class="" v-show="conn.expanded">
                <td colspan="2">
                  <div class="row no-gutters">
                    <div class="col-2">#{{roomindex}}</div>
                    <div class="col-2">Room</div>
                    <div class="col-8">{{room}}</div>
                  </div>
                </td>
              </tr>
              <tr :key="`${conn.id}-spacer`" class="" v-show="conn.expanded">
                <td colspan="2">&nbsp;</td>
              </tr>
            </template>
          </template>
        </CollapsibleTable>
      </TabPage>

      <TabPage tabid="tab-jobqueue">
        <ContentTitle>Job Scheduler Queue</ContentTitle>
        <CollapsibleTable>
          <template v-slot:header>
            <tr>
              <th>Job</th>
              <th width="50%">Stats</th>
            </tr>
          </template>
          <template v-slot:body>
            <template v-for="(queue, index) in jobQueueList">
              <CollapsibleRow :key="index">
                <td class="font-weight-bold" @click="queue.expanded = !queue.expanded">
                  <CollapsibleRowArrow :expanded="queue.expanded" />
                  {{queue.name}}
                </td>
                <td @click="queue.expanded = !queue.expanded">
                  Waiting: {{queue.stats.waiting}},&nbsp;&nbsp;
                  Active: {{queue.stats.active}},&nbsp;&nbsp;
                  Completed: {{queue.stats.completed}},&nbsp;&nbsp;
                  Failed: {{queue.stats.failed}},&nbsp;&nbsp;
                  Delayed: {{queue.stats.delayed}}
                </td>
              </CollapsibleRow>
              <tr v-for="(job, jobindex) in queue.jobs" :key="`${queue.name}-${job.id}`" class="" v-show="queue.expanded">
                <td colspan="2">
                  <div class="row no-gutters">
                    <div class="col-2">#{{jobindex}}</div>
                    <div class="col-2">ID</div>
                    <div class="col-8">{{job.id}}</div>
                  </div>
                  <div class="row no-gutters">
                    <div class="col-2"> </div>
                    <div class="col-2">Data</div>
                    <div class="col-8 debug-output">{{objectToJSON(job.data)}}</div>
                  </div>
                  <div class="row no-gutters">
                    <div class="col-2"> </div>
                    <div class="col-2">Opts</div>
                    <div class="col-8 debug-output">{{objectToJSON(job.opts)}}</div>
                  </div>
                  <div class="row no-gutters">
                    <div class="col-2"> </div>
                    <div class="col-2">Stack</div>
                    <div class="col-8 debug-output">{{objectToJSON(job.stacktrace)}}</div>
                  </div>
                </td>
              </tr>
              <tr :key="`${queue.name}-spacer`" class="" v-show="queue.expanded">
                <td colspan="2">&nbsp;</td>
              </tr>
            </template>
          </template>
        </CollapsibleTable>    
      </TabPage>

      <TabPage tabid="tab-automation">
        <div class="row no-gutter">
          <div class="col-lg-6">
            <ContentTitle>Automation Stats</ContentTitle>
          </div>
          <div class="col-lg-6">
            <ReportFilterPopover class="float-right" sizeClass="popover-xlarge">
              <template v-slot:popover>
                <h3>Date Range</h3>
                <ReportDateRangeSelect :startDate="automationStatStartDate" :endDate="automationStatEndDate" @updateStartDate="automationStatStartDate = $event" @updateEndDate="automationStatEndDate = $event" />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{automationStatStartDate | formatDate}} - {{automationStatEndDate | formatDate}}
                </div>
              </template>
            </ReportFilterPopover>
          </div>
        </div>

        <ContentTitle>Queue Stats</ContentTitle>
        <div class="row feed-element">
          <div class="col-12">
            <div class="well debug-output">
              Stats for date range:<br />
              Waiting Count: {{automationStats.waitingCount}}<br />
              Processing Count: {{automationStats.processingCount}}<br />
              Processed Count: {{automationStats.processedCount}}
            </div>
          </div>
        </div>

        <br />
        <FormHorizontalRule />
        <br />

        <ContentTitle>Pending Queue Items</ContentTitle>
        <StaticDataTable class="static-table-list" variant="table-sm">
          <template v-slot:header>
            <tr>
              <th>Queue Id</th>
              <th>Trigger Component</th>
              <th>Trigger Data</th>
              <th>Timestamp</th>
              <th>Account</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="queueItem of automationPendingList" :key="queueItem.id">
              <td class="font-weight-bold">{{queueItem.id}}</td>
              <td>{{queueItem.autoComponentType.type}} - {{queueItem.autoComponentType.name}}</td>
              <td>{{queueItem.triggerData}}</td>
              <td>{{formatDateCustom(queueItem.createdAt, 'DD/MM/YY HH:mm:ss')}}</td>
              <td>{{queueItem.accountId}}</td>
            </tr>
          </template>
        </StaticDataTable>

        <br />
        <FormHorizontalRule />
        <br />

        <ContentTitle>Recent Rule Log Warnings</ContentTitle>
        <StaticDataTable class="static-table-list" variant="table-sm">
          <template v-slot:header>
            <tr>
              <th>Message</th>
              <th>Details</th>
              <th>Rule</th>
              <th>Timestamp</th>
              <th>Account</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="autoRuleLog of automationStats.recentRuleWarningList" :key="autoRuleLog.id">
              <td>{{autoRuleLog.message}}</td>
              <td>{{autoRuleLog.details}}</td>
              <td>{{autoRuleLog.autoRule.id}} - {{autoRuleLog.autoRuleLog.name}}</td>
              <td>{{formatDateCustom(autoRuleLog.createdAt, 'DD/MM/YY HH:mm:ss')}}</td>
              <td>{{autoRuleLog.accountId}}</td>
            </tr>
          </template>
        </StaticDataTable>

        <br />
        <FormHorizontalRule />
        <br />

        <ContentTitle>Recent Rule Log Errors</ContentTitle>
        <StaticDataTable class="static-table-list" variant="table-sm">
          <template v-slot:header>
            <tr>
              <th>Message</th>
              <th>Details</th>
              <th>Rule</th>
              <th>Timestamp</th>
              <th>Account</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="autoRuleLog of automationStats.recentRuleErrorList" :key="autoRuleLog.id">
              <td>{{autoRuleLog.message}}</td>
              <td>{{autoRuleLog.details}}</td>
              <td>{{autoRuleLog.autoRule.id}} - {{autoRuleLog.autoRule.name}}</td>
              <td>{{formatDateCustom(autoRuleLog.createdAt, 'DD/MM/YY HH:mm:ss')}}</td>
              <td>{{autoRuleLog.accountId}}</td>
            </tr>
          </template>
        </StaticDataTable>
      </TabPage>

      <TabPage tabid="tab-cache">
        <div class="row no-gutter">
          <div class="col-6">
            <ContentTitle>Redis Details</ContentTitle>
          </div>
          <div class="col-6">
            <ContentTitle>Keys</ContentTitle>
          </div>
        </div>
        <div class="row no-gutter">
          <div class="col-6">
            <StaticDataTable class="static-table-list" smallRows v-if="cacheInfo.groups">
              <template v-slot:header>
                <tr>
                  <th>Key</th>
                  <th>Value</th>
                </tr>
              </template>
              <template v-slot:body>
                <template v-for="cacheInfoGroup of cacheInfo.groups">
                  <tr :key="`${cacheInfoGroup.name}`"><td colspan="2" class="font-weight-bold">{{cacheInfoGroup.name}}</td></tr>

                  <tr v-for="groupValue in cacheInfoGroup.values" :key="`${cacheInfoGroup.name}-${groupValue.key}`">
                    <td>{{groupValue.key}}</td>
                    <td>{{groupValue.value}}</td>
                  </tr>
                  <tr :key="`${cacheInfoGroup.name}-spacer`"><td colspan="2"> </td></tr>
                </template>
              </template>
            </StaticDataTable>
          </div>
          <div class="col-6">
            <StaticDataTable class="static-table-list" smallRows v-if="cacheInfo.redis && cacheInfo.redis.keys">
              <template v-slot:header>
                <tr>
                  <th>Key</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="key in cacheInfo.redis.keys" :key="key">
                  <td>{{key}}</td>
                </tr>
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>


      <TabPage tabid="tab-tools">
        <ContentTitle>Tools</ContentTitle>

        <FormButton @click="resyncBillingProducts" class="btn btn-primary btn-sm"><i class="far fa-sync" /> Resync Stripe Products</FormButton>
        <br /><br />
        
      </TabPage>

    </template>
  </TabbedBox>
</template>

<style scoped>
.debug-output {
  white-space: pre;
  line-height: 18px;
  font-size: 12px;
  font-family: monospace, monospace;
  overflow-x: auto
}
.system-log-highlight {
  background-color: #ffffdf !important;
  font-weight: 600 !important;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import form from '@/mixins/form'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import Popover from '@/components/popover'

export default {
  name: "Developer",
  mixins: [form],
  components: {
    ReportFilterPopover,
    ReportDateRangeSelect,
    Popover
  },
  data() {
    return {
      systemInfoList: [],
      systemReportingInfoList: [],
      systemErrorLogs: [],
      systemWarningLogs: [],
      selectedLog: {},
      showWarnings: false,
      jobQueueList: [],
      socketConnList: [],
      automationPendingList: [],
      automationStats: {
        waitingCount: 0,
        processingCount: 0,
        processedCount: 0,
        recentRuleWarningList: [],
        recentRuleErrorList: []
      },
      cacheInfo: {},

      automationStatStartDate: moment().subtract(14, 'days').toISOString(),
      automationStatEndDate: moment().toISOString(),
    }
  },
  watch: {
    automationStatStartDate: function() { this.reloadAutomationsInfo() },
    automationStatEndDate: function() { this.reloadAutomationsInfo() },
  },
  mounted() {
    this.$apiService.developerService.developerGetSystemInfo()
    .then(response => {
      this.$data.systemInfoList = response.data.systemInfo
      this.$data.socketConnList = response.data.socketConnList.map(conn => {
        conn.expanded = false
        return conn
      })
    })

    this.$apiService.developerService.developerGetReportingSystemInfo()
    .then(response => {
      this.$data.systemReportingInfoList = response.data.systemInfo
    })

    this.$apiService.developerService.developerGetCacheInfo()
    .then(response => {
      this.cacheInfo = response.data

      if(this.cacheInfo.redis.info) {
        this.cacheInfo.groups = []
        let curGroup = null
        _.compact(this.cacheInfo.redis.info.split('\r\n'))
        .map(val => {
          return val.split(':')
        })
        .forEach(val => {
          if(val.length == 1) {
            if(curGroup) {
              this.cacheInfo.groups.push(curGroup)
            }
            curGroup = {
              name: val[0].replace('#', '').trim(),
              values: []
            }
          } else if(val.length == 2) {
            if(curGroup) {
              curGroup.values.push({ key: val[0], value: val[1] })
            }
          }
        })
        if(curGroup)
          this.cacheInfo.groups.push(curGroup)
      }
    })

    this.reloadSystemLogs()
    this.reloadJobQueueList()
    this.reloadAutomationsInfo()
  },
  beforeDestroy () {
  },
  computed: {
    frontendDetails() {
      return [
        `VERSION: ${process.env.VERSION}`,
        `BUILD_DATE: ${process.env.BUILD_DATE}`,
        `BASE_URL: ${process.env.BASE_URL}`,
        `VUE_APP_API_URL: ${process.env.VUE_APP_API_URL}`,
        `VUE_APP_REPORTING_API_URL: ${process.env.VUE_APP_REPORTING_API_URL}`,
        `VUE_APP_SOCKET_URL: ${process.env.VUE_APP_SOCKET_URL}`,
        `VUE_APP_LIVE: ${process.env.VUE_APP_LIVE}`,
        `VUE_APP_LOCAL: ${process.env.VUE_APP_LOCAL}`
      ].join('\n')
    },
  },
  methods: {
    reloadJobQueueList() {
      this.$apiService.developerService.developerGetJobQueueList()
      .then(response => {

        this.$data.jobQueueList = response.data.map(queue => {

          var existingItem = this.$data.jobQueueList.find(item => item.name == queue.name)
          if(existingItem)
            queue.expanded = existingItem.expanded
          else
            queue.expanded = false
          
          return queue
        })
      })
    },
    reloadAutomationsInfo() {
      let params = {
        startDate: this.automationStatStartDate,
        endDate: this.automationStatEndDate
      }

      this.$apiService.developerService.developerGetAutomationInfo(params)
      .then(response => {
        this.automationPendingList = response.data.automationPendingList
        
        this.automationStats.waitingCount = response.data.automationStats.waitingCount
        this.automationStats.processingCount = response.data.automationStats.processingCount
        this.automationStats.processedCount = response.data.automationStats.processedCount
        this.automationStats.recentRuleWarningList = response.data.automationStats.recentRuleWarningList
        this.automationStats.recentRuleErrorList = response.data.automationStats.recentRuleErrorList
      })
    },
    reloadSystemLogs() {
      this.$apiService.developerService.developerGetSystemLogList({ filter: { type: ['error', 'critical'] }, limit: 100 })
      .then(response => {
        this.$data.systemErrorLogs = response.data
      })
      this.$apiService.developerService.developerGetSystemLogList({ filter: { type: 'warning' }, limit: 100 })
      .then(response => {
        this.$data.systemWarningLogs = response.data
      })
    },
    isSystemLogToday(log) {
      return moment(log.createdAt).isAfter(moment().local().startOf('day'))
    },

    systemLogLabelClassForType(type) {
      switch(type) {
        case 'critical': return 'label-danger'
        case 'error': return 'label-danger'
        case 'warning': return 'label-warning'
        case 'info': return 'label-success'
      }
      return ''
    },
    objectToJSON(obj) {
      return JSON.stringify(obj || {}, null, 2)
    },
    resyncBillingProducts() {
      this.$api.post('/developer/billing/resyncproducts')
      .then(response => {
        this.successToast('Resynced')
      })
      .catch(err => {
        this.displayNetworkError(err)
      })
    },
    truncateLogDetails(logText) {
      return ('' + logText).substring(0, 100)
    },
    displaySystemLog(evt, log) {
      this.selectedLog = log
      this.$refs.logSystemPopover.displayFromElement(evt.target)
    },
    displayWarningLog(evt, log) {
      this.selectedLog = log
      this.$refs.logWarningPopover.displayFromElement(evt.target)
    }
  }
}

</script>